import { ColumnDef, flexRender, getCoreRowModel, useReactTable, RowData } from '@tanstack/react-table'
import '@tanstack/react-table'
import { Dispatch, SetStateAction } from 'react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'

declare module '@tanstack/react-table' {
	interface TableMeta<TData extends RowData> {
		actions: {
			deleteMutation: (rowId: string) => void
		}
	}
}

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[]
	data: TData[]
	rowCount: number
	deleteMutation: (rowId: string) => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	setPagination: Dispatch<
		SetStateAction<{
			pageIndex: number
			pageSize: number
		}>
	>
}

export function DataTable<TData, TValue>({
	columns,
	data,
	rowCount,
	pagination,
	setPagination,
	deleteMutation,
}: DataTableProps<TData, TValue>) {
	const table = useReactTable({
		data,
		columns,
		manualPagination: true,
		getCoreRowModel: getCoreRowModel(),
		rowCount: rowCount,
		onPaginationChange: setPagination,
		state: {
			pagination,
		},
		meta: {
			actions: {
				deleteMutation,
			},
		},
	})

	return (
		<div className="rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map(headerGroup => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map(header => {
								return (
									<TableHead key={header.id} align="center">
										{header.isPlaceholder
											? null
											: flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								)
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map(row => (
							<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
								{row.getVisibleCells().map(cell => (
									<TableCell key={cell.id} align="center">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								No conversations have been added yet.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
			<div className="flex items-center justify-end space-x-2 py-4 mr-2">
				<span className="flex items-center gap-1">
					<div>Page</div>
					<strong>
						{table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
					</strong>
				</span>
				<Button
					variant="outline"
					size="sm"
					onClick={() => table.previousPage()}
					disabled={!table.getCanPreviousPage()}
				>
					Previous
				</Button>
				<Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
					Next
				</Button>
			</div>
		</div>
	)
}
